// ProfilePicture.js - With immediate image update after upload
import React, { useState, useEffect } from 'react';
import './ProfilePicture.css';

function ProfilePicture({ user, editable = false, onUpdate = null }) {
  const [showModal, setShowModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [profileImageSrc, setProfileImageSrc] = useState('/default-profile.png');
  const [notification, setNotification] = useState({ show: false, message: '' });
  
  // Set the profile image source when user data changes
  useEffect(() => {
    updateProfileImage();
  }, [user]);
  
  // Update profile image source based on user data
  const updateProfileImage = () => {
    if (user && user.profilePicture && user.profilePicture.path) {
      const imagePath = user.profilePicture.path;
      console.log('Profile image path:', imagePath);
      
      // Construct full URL for image
      const imageUrl = `/uploads/${imagePath}`;
      console.log('Full image URL:', imageUrl);
      
      // Test if the image exists
      const img = new Image();
      img.onload = () => {
        console.log('Profile image loaded successfully');
        setProfileImageSrc(imageUrl);
      };
      img.onerror = () => {
        console.error('Failed to load profile image from:', imageUrl);
        setProfileImageSrc('/default-profile.png');
      };
      img.src = imageUrl;
    } else {
      console.log('No profile picture found, using default');
      setProfileImageSrc('/default-profile.png');
    }
  };

  // Function to show notification
  const showNotification = (message) => {
    setNotification({ show: true, message });
    setTimeout(() => {
      setNotification({ show: false, message: '' });
    }, 2000);
  };
  
  const handleClick = () => {
    if (editable) {
      setShowModal(true);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  
  const handleFile = (file) => {
    // Check if file is an image
    if (!file.type.match('image.*')) {
      showNotification('Please select an image file');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage({
        dataUrl: e.target.result,
        file: file // Store the original file object
      });
    };
    reader.readAsDataURL(file);
  };
  
  const handleUpload = async () => {
    if (!previewImage || !previewImage.file) {
      showNotification('Please select an image first');
      return;
    }
    
    try {
      setUploading(true);
      console.log('Starting upload process...');
      
      // Create form data directly with the file object
      const formData = new FormData();
      formData.append('profilePicture', previewImage.file);
      console.log('FormData created with file object');
      
      // Upload to server
      const token = localStorage.getItem('token');
      console.log('Sending request to server...');
      const uploadResponse = await fetch('/api/users/profile-picture', {
        method: 'POST',
        headers: {
          'x-auth-token': token
        },
        body: formData
      });
      
      console.log('Server response status:', uploadResponse.status);
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Server error response:', errorText);
        throw new Error(`Failed to upload profile picture: ${errorText}`);
      }
      
      const data = await uploadResponse.json();
      console.log('Server response data:', data);
      
      // Create full URL for the newly uploaded image
      const newImageUrl = `/uploads/${data.path}`;
      
      // Update the displayed profile image immediately with the preview
      // (this will show immediately while we wait for the server image to be ready)
      setProfileImageSrc(previewImage.dataUrl);
      
      // Load the actual server image in the background
      const img = new Image();
      img.onload = () => {
        console.log('New profile image loaded from server');
        // Once loaded, update to the actual server URL (which might be cached now)
        setProfileImageSrc(newImageUrl + '?t=' + new Date().getTime());
      };
      img.src = newImageUrl;
      
      // Update user in local storage with new profile picture
      const currentUser = JSON.parse(localStorage.getItem('user'));
      currentUser.profilePicture = data;
      localStorage.setItem('user', JSON.stringify(currentUser));
      console.log('Updated user in localStorage');
      
      // Call onUpdate if provided
      if (onUpdate) {
        onUpdate({
          ...currentUser,
          profilePicture: data
        });
        console.log('Called onUpdate with updated user');
      }
      
      // Close modal
      setShowModal(false);
      setPreviewImage(null);
      showNotification('Profile picture updated successfully');
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Error: ' + error.message);
    } finally {
      setUploading(false);
    }
  };
  
  const handleCancel = () => {
    setShowModal(false);
    setPreviewImage(null);
  };
  
  return (
    <>
      {notification.show && (
        <div className="notification">
          {notification.message}
        </div>
      )}
      
      <div 
        className={`profile-picture ${editable ? 'editable' : ''}`}
        onClick={handleClick}
      >
        <img 
          src={profileImageSrc} 
          alt="Profile" 
          onError={(e) => {
            console.error('Error loading profile image');
            e.target.src = '/default-profile.png';
          }}
        />
        {editable && <div className="edit-overlay">Edit</div>}
      </div>
      
      {showModal && (
        <div className="profile-picture-modal-overlay">
          <div className="profile-picture-modal">
            <h3>Update Profile Picture</h3>
            
            <div 
              className={`drop-zone ${dragActive ? 'active' : ''} ${previewImage ? 'has-preview' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {previewImage ? (
                <div className="profile-preview-container">
                  <img src={previewImage.dataUrl} alt="Preview" className="profile-preview-image" />
                </div>
              ) : (
                <>
                  <p>Drag & drop an image here or</p>
                  <label className="file-input-label">
                    Browse
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileInput} 
                      className="file-input"
                    />
                  </label>
                </>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-button" 
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="upload-button" 
                onClick={handleUpload}
                disabled={!previewImage || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProfilePicture;